/**
 * Single owner of the restaurant profile page's JS chunk.
 *
 * Why this exists: the profile screen used to be a bare `React.lazy` inside the
 * route's wrapper component, so neither the router's chunk preload (it needs a
 * `.preload` on the route component) nor anything else could fetch it ahead of
 * time. The chunk only started downloading when the wrapper first rendered,
 * inside the view transition's update callback, so the transition's "new"
 * snapshot was a spinner and the shared-element photo/title morph was lost on
 * a cold first tap.
 *
 * The route now builds its component with `lazyRouteComponent` over
 * `loadRestaurantProfileChunk`, so the router awaits the chunk while it loads
 * the route (before it starts the view transition), and
 * `warmRestaurantProfileChunk` downloads it quietly ahead of the first tap.
 *
 * Deliberately free of any `@tanstack/react-router` import: RestaurantCard
 * imports `isRestaurantProfileChunkReady` and its tests mock that module.
 */

type ProfileModule = typeof import('@/pages/restaurant');

const defaultImporter = (): Promise<ProfileModule> => import('@/pages/restaurant');

let importer = defaultImporter;
let chunkPromise: Promise<ProfileModule> | undefined;
let chunkReady = false;

/**
 * Starts (or joins) the chunk download. Safe to call any number of times: the
 * same promise is shared by the router's own preload and the idle warm-up. A
 * failed download clears the cached promise here, so a later direct caller
 * (the router's preload on tap) retries the import. The idle warm-up does not
 * retry (see `warmRestaurantProfileChunk`).
 *
 * A falsy module counts as a failure, not a success: in production Vite's
 * dynamic-import helper resolves to `undefined` when a `vite:preloadError`
 * listener calls `preventDefault()` (which `installPreloadErrorReload` does),
 * so the import "succeeds" with no module. Marking that ready would tell
 * RestaurantCard the chunk is cached when it is not.
 *
 * That retry does NOT reach the router: `lazyRouteComponent` (router-core
 * 1.139.12) keeps a failed load's error for the rest of the session, so after
 * one failed load every profile visit rethrows it until a full page reload.
 * Only "module not found" style messages make the router reload once; Vite's
 * "Unable to preload CSS" does not match. `installPreloadErrorReload`
 * (`vite:preloadError`) is the global recovery for that case.
 */
export function loadRestaurantProfileChunk(): Promise<ProfileModule> {
  if (!chunkPromise) {
    chunkPromise = importer().then(
      (mod) => {
        if (!mod) {
          chunkPromise = undefined;
          throw new Error('Restaurant profile chunk resolved without a module');
        }
        chunkReady = true;
        return mod;
      },
      (error: unknown) => {
        chunkPromise = undefined;
        throw error;
      },
    );
  }
  return chunkPromise;
}

/** True once the profile chunk has finished downloading in this session. */
export function isRestaurantProfileChunkReady(): boolean {
  return chunkReady;
}

/** Upper bound for the idle callback so a busy main thread cannot starve the warm-up. */
const WARM_IDLE_TIMEOUT_MS = 300;

let warmScheduled = false;

/**
 * Downloads the profile chunk once per session, at idle. No API calls, errors
 * swallowed. Safari has no `requestIdleCallback`, so it falls back to a short
 * `setTimeout`.
 *
 * A failed warm-up is NOT retried for the rest of the session: `warmScheduled`
 * stays true. In production a failed dynamic import fires `vite:preloadError`,
 * and `installPreloadErrorReload` reloads the page (30 s cooldown). Retrying
 * from every later mount of a warming surface (feed post, list card, check-in
 * row, Search) would re-trigger that reload after each cooldown, which offline
 * means reloading into a browser error page. The router's own preload when the
 * member taps a restaurant remains the fallback path.
 */
export function warmRestaurantProfileChunk(): void {
  if (warmScheduled || chunkReady || typeof window === 'undefined') return;
  warmScheduled = true;

  const run = () => {
    loadRestaurantProfileChunk().catch(() => {
      // Intentionally silent: a failed warm-up must never surface to a member,
      // and must not re-arm (see the function comment).
    });
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: WARM_IDLE_TIMEOUT_MS });
  } else {
    window.setTimeout(run, 1);
  }
}

/** Test-only: resets module state between cases. */
export function __resetRestaurantProfileChunkForTests(): void {
  importer = defaultImporter;
  chunkPromise = undefined;
  chunkReady = false;
  warmScheduled = false;
}

/** Test-only: replaces the dynamic import (e.g. to resolve `undefined` like Vite's preload helper). */
export function __setRestaurantProfileImporterForTests(next: () => Promise<ProfileModule>): void {
  importer = next;
}
